import { useEffect, useMemo, useRef, useState, type MouseEventHandler } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import type { TooltipVisibleChangeHandler } from '../index.tsx';

interface UseCopyTooltipStateProps {
  duration?: number;
  onCopyClick?: MouseEventHandler<HTMLElement>;
}

interface UseCopyTooltipStateReturn {
  feedbackVisible: boolean;
  onCopyClick: MouseEventHandler<HTMLElement>;
  hintVisible: boolean;
  onHintVisibleChange: TooltipVisibleChangeHandler;
}

export const DEFAULT_FEEDBACK_DISPLAY_DURATION_MS = 3000;

export const useCopyTooltipState = ({
  duration = DEFAULT_FEEDBACK_DISPLAY_DURATION_MS,
  onCopyClick,
}: UseCopyTooltipStateProps): UseCopyTooltipStateReturn => {
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [hintVisible, setHintVisible] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopyClick = useCallbackRef<MouseEventHandler<HTMLElement>>((event) => {
    event.stopPropagation();
    event.preventDefault();

    setHintVisible(false);
    setFeedbackVisible(true);

    timeoutRef.current = setTimeout(() => {
      setFeedbackVisible(false);
    }, duration);

    onCopyClick?.(event);
  });

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useMemo<UseCopyTooltipStateReturn>(
    () => ({
      feedbackVisible: feedbackVisible,
      hintVisible: hintVisible && !feedbackVisible,
      onCopyClick: handleCopyClick,
      onHintVisibleChange: setHintVisible,
    }),
    [handleCopyClick, feedbackVisible, hintVisible],
  );
};
